import React, {useCallback, useState} from 'react';
import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconInfoCircle, IconPlus} from '@tabler/icons-react';
import type {
  WorkflowDefinitionTriggerResponse} from 'src/api/ProactApiClient';
import {
  IntervalTypeEnum,
  ItemAssignmentStateEnum,
  WorkflowDefinitionTriggerTypeEnum,
  WorkflowTrainingEventTypeEnum,
} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import type {
  IClientKeyed} from 'src/app/workflows/clientIdentity';
import {
  itemKey,
  nextClientKey,
} from 'src/app/workflows/clientIdentity';
import {TriggerCard} from './TriggerCard';
import {GetWorkflowTriggerLabel} from 'src/app/workflows/workflowHelpers';

export interface TriggerListProps {
  triggers: WorkflowDefinitionTriggerResponse[];
  onTriggersChange: (triggers: WorkflowDefinitionTriggerResponse[]) => void;
  projectId?: number;
}

export const TriggerList: React.FC<TriggerListProps> = ({
  triggers,
  onTriggersChange,
  projectId,
}) => {
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [expandedSet, setExpandedSet] = useState<Set<string | number>>(
    () => new Set<string | number>()
  );

  const toggleExpanded = useCallback((key: string | number) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSet(new Set(triggers.map((trigger, i) => itemKey(trigger, i))));
  }, [triggers]);

  const collapseAll = useCallback(() => {
    setExpandedSet(new Set());
  }, []);

  const handleAddNew = () => {
    const clientKey = nextClientKey();
    onTriggersChange([
      ...triggers,
      {
        clientKey,
        type: WorkflowDefinitionTriggerTypeEnum.Schedule,
        scheduleIntervalType: IntervalTypeEnum.Daily,
        scheduleStartDate: new Date(),
        scheduleEndDate: new Date(),
        triggerTrainingEventType: WorkflowTrainingEventTypeEnum.Completed,
        triggerItemAssignmentState: ItemAssignmentStateEnum.Assigned,
        metadata: {},
      } as WorkflowDefinitionTriggerResponse & IClientKeyed,
    ]);
    setExpandedSet(prev => new Set(prev).add(clientKey));
  };

  const handleTriggerChange = (
    trigger: WorkflowDefinitionTriggerResponse,
    index: number
  ) => {
    const newTriggers = Array.from(triggers);
    newTriggers[index] = trigger;
    onTriggersChange(newTriggers);
  };

  const handleDeleteRequest = (index: number) => {
    setDeleteIndex(index);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex !== null) {
      onTriggersChange([
        ...triggers.slice(0, deleteIndex),
        ...triggers.slice(deleteIndex + 1),
      ]);
      setDeleteIndex(null);
    }
  };

  const deleteTriggerLabel =
    deleteIndex !== null
      ? GetWorkflowTriggerLabel(triggers[deleteIndex])
      : '';

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={4}>Triggers</Title>
          <Text size="sm" c="dimmed">
            Each trigger defines a way for this workflow to be initiated — by an
            event in the system or on a recurring schedule.
          </Text>
        </div>
        {triggers.length > 1 && (
          <Group gap="xs">
            <Button variant="subtle" size="xs" onClick={expandAll}>
              Expand All
            </Button>
            <Button variant="subtle" size="xs" onClick={collapseAll}>
              Collapse All
            </Button>
          </Group>
        )}
      </Group>

      {projectId ? (
        <Alert
          title="Project-based Workflow"
          color="blue"
          icon={<IconInfoCircle />}>
          Event-driven triggers will only fire for users with access to the
          selected project.
        </Alert>
      ) : (
        <Text size="xs" c="dimmed">
          No project is selected. Event-driven triggers will fire for all users
          in the instance.
        </Text>
      )}

      {triggers.length === 0 ? (
        <Alert variant="light" color="gray" title="No triggers configured">
          <Text size="sm" mb="sm">
            Triggers define when this workflow should run. Add at least one
            trigger to activate the workflow.
          </Text>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddNew}
            size="sm">
            Add First Trigger
          </Button>
        </Alert>
      ) : (
        <>
          {triggers.map((trigger, index) => {
            const key = itemKey(trigger, index);
            return (
              <TriggerCard
                key={key}
                trigger={trigger}
                index={index}
                expanded={expandedSet.has(key)}
                onToggleExpanded={() => toggleExpanded(key)}
                onTriggerChange={handleTriggerChange}
                onDeleteRequest={handleDeleteRequest}
              />
            );
          })}
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddNew}
            size="sm"
            w="fit-content">
            Add Trigger
          </Button>
        </>
      )}

      <ConfirmDialog
        opened={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={handleDeleteConfirm}
        title={`Delete Trigger ${deleteIndex !== null ? deleteIndex + 1 : ''}?`}
        body={`Are you sure you want to delete the "${deleteTriggerLabel}" trigger? This cannot be undone.`}
        confirmationButtonColor="red"
        buttonTextYes="Delete"
        buttonTextNo="Cancel"
      />
    </Stack>
  );
};
